import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { Notification } from '../../types';
import { formatDateTimeArgentina } from '../../utils/dateFormatter';
import { hasPermission } from '../../utils/permissions';
import { FaBell } from 'react-icons/fa';

/** Formatea mensaje: si viene "titulo|||mensaje" muestra título y mensaje; si no, el texto tal cual */
const formatNotificationMessage = (message: string): { title?: string; text: string } => {
    if (!message || typeof message !== 'string') return { text: '' };
    if (message.includes('|||')) {
        const [title, ...rest] = message.split('|||');
        return { title: title?.trim() || undefined, text: rest.join('|||').trim() || title?.trim() || '' };
    }
    return { text: message };
};

const NotificationBell: React.FC = () => {
    const { user } = useAuth();
    const { notifications, unreadCount, markNotificationAsRead, markAllNotificationsAsRead } = useNotification();
    const navigate = useNavigate();
    
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    // Posición del dropdown en coordenadas de viewport (portal + position:fixed).
    // Con esto el dropdown "escapa" de cualquier stacking context o overflow del layout
    // y siempre se pinta por encima de todo el contenido (dashboards, cards, etc.).
    const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Lista ordenada por fecha (más nuevas arriba); el backend ya suele venir ordenado, pero por si llegan por socket
    const sortedNotifications = useMemo(() => {
        return [...notifications].sort((a, b) => {
            const da = a.created_at ? new Date(a.created_at).getTime() : 0;
            const db = b.created_at ? new Date(b.created_at).getTime() : 0;
            return db - da;
        });
    }, [notifications]);

    // Calcula la posición del dropdown alineado al borde derecho del botón, justo debajo.
    const updateDropdownPos = useCallback(() => {
        const btn = buttonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        setDropdownPos({
            top: rect.bottom + 8, // 8px = mt-2
            right: window.innerWidth - rect.right,
        });
    }, []);

    // Recalcular posición al abrir, y en scroll/resize mientras esté abierto.
    useLayoutEffect(() => {
        if (!isDropdownOpen) return;
        updateDropdownPos();
        const onScroll = () => updateDropdownPos();
        const onResize = () => updateDropdownPos();
        window.addEventListener('scroll', onScroll, true); // capture: escuchar scrolls internos
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [isDropdownOpen, updateDropdownPos]);

    // Cerrar al hacer click fuera (considerando que el dropdown vive en un Portal).
    useEffect(() => {
        if (!isDropdownOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedButton = buttonRef.current?.contains(target);
            const clickedDropdown = dropdownRef.current?.contains(target);
            if (!clickedButton && !clickedDropdown) {
                setIsDropdownOpen(false);
            }
        };
        // Cerrar con Escape para accesibilidad.
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [isDropdownOpen]);

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.id) return; // Evitar error undefined

        if (!notification.is_read) {
            markNotificationAsRead(notification.id);
        }

        if (notification.related_type === 'ticket' && notification.related_id) {
            const basePath = user?.role === 'admin' ? '/admin' : (user?.role === 'agent' || user?.role === 'supervisor' || user?.role === 'viewer') ? '/agent' : '/client';
            navigate(`${basePath}/tickets/${notification.related_id}`);
        } else if (notification.related_type === 'repair_order' && notification.related_id) {
            const basePath = user?.role === 'admin' ? '/admin' : (user?.role === 'agent' || user?.role === 'supervisor') ? '/agent' : '/client';
            navigate(`${basePath}/repair-orders/${notification.related_id}`);
        } else if (notification.related_type === 'activation') {
            const basePath =
                user?.role === 'admin'
                    ? '/admin'
                    : user?.role === 'agent' || user?.role === 'supervisor' || user?.role === 'viewer'
                      ? '/agent'
                      : null;
            if (basePath) {
                navigate(`${basePath}/activations`);
            }
        } else if (notification.related_type === 'payment' && notification.related_id) {
            const canReview =
                user?.role === 'admin' ||
                ((user?.role === 'agent' || user?.role === 'supervisor' || user?.role === 'viewer') &&
                    hasPermission(user.permissions, 'payments_review'));
            if (canReview) {
                const staffBase = user?.role === 'admin' ? '/admin' : '/agent';
                navigate(`${staffBase}/payments?paymentId=${notification.related_id}`);
            } else {
                toast.info('No tenés permiso para revisar pagos. Pedile al administrador que active "Pagos pendientes".');
            }
        }
        setIsDropdownOpen(false);
    };

    if (!user) return null;

    // El dropdown se renderiza en un Portal en <body> con position: fixed. Así queda
    // fuera del stacking context del <header>/<main> del Layout y siempre aparece por
    // encima de las cards del dashboard (fix del bug de notificaciones detrás de elementos).
    const dropdown = isDropdownOpen && dropdownPos ? createPortal(
        <div
            ref={dropdownRef}
            role="dialog"
            aria-label="Notificaciones"
            className="fixed w-80 md:w-96 bg-white rounded-lg shadow-2xl border border-gray-200 py-1 max-h-96 overflow-y-auto z-[9999]"
            style={{ top: dropdownPos.top, right: dropdownPos.right }}
        >
            <div className="flex justify-between items-center px-4 py-2 text-sm text-gray-700 border-b bg-gray-50 sticky top-0 z-10">
                <span className="font-bold">Notificaciones</span>
                {unreadCount > 0 && (
                    <button type="button" onClick={() => markAllNotificationsAsRead()} className="text-blue-600 hover:underline text-xs font-medium">
                        Marcar todas como leídas
                    </button>
                )}
            </div>
            {sortedNotifications.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                    <p>No tienes notificaciones nuevas.</p>
                </div>
            ) : (
                sortedNotifications.map((notification) => {
                    const { title, text } = formatNotificationMessage(notification.message);
                    return (
                        <div
                            key={notification.id ?? `n-${notification.created_at}-${Math.random()}`}
                            role="button"
                            tabIndex={0}
                            className={`block px-4 py-3 text-sm border-b cursor-pointer transition ${
                                notification.is_read ? 'bg-white text-gray-600 hover:bg-gray-50' : 'bg-blue-50 text-gray-800 font-medium hover:bg-blue-100'
                            }`}
                            onClick={() => handleNotificationClick(notification)}
                            onKeyDown={(e) => e.key === 'Enter' && handleNotificationClick(notification)}
                        >
                            {title && <p className="font-semibold text-gray-800 leading-tight">{title}</p>}
                            <p className="leading-tight">{text || notification.message}</p>
                            <span className="text-xs text-gray-400 block mt-1">
                                {notification.created_at ? formatDateTimeArgentina(notification.created_at) : 'Reciente'}
                            </span>
                        </div>
                    );
                })
            )}
        </div>,
        document.body
    ) : null;

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="dialog"
                aria-expanded={isDropdownOpen}
                onClick={() => setIsDropdownOpen((v) => !v)}
                className="relative p-2 text-gray-500 hover:text-blue-600 transition focus:outline-none"
            >
                <FaBell size={22} />
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/4 -translate-y-1/4 bg-red-600 rounded-full">
                        {unreadCount}
                    </span>
                )}
            </button>
            {dropdown}
        </div>
    );
};

export default NotificationBell;